/**
 * Users Feature Constants
 * All hard-coded values, configuration options, and enums for the users feature
 */

// ============================================================================
// USER ROLES
// ============================================================================

/**
 * User role options
 * Use translation keys for labels: t(`role.${value}`)
 */
export const USER_ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  CLIENT_SUPPORT: "clientSupport",
  CLIENT: "client",
  LIVREUR: "livreur",
  ACCOUNTANT: "accountant",
};

/**
 * User role options for select dropdowns
 */
export const USER_ROLE_OPTIONS = [
  { value: "admin", label: "role.admin" },
  { value: "manager", label: "role.manager" },
  { value: "clientSupport", label: "role.clientSupport" },
  { value: "client", label: "role.client" },
  { value: "livreur", label: "role.livreur" },
  { value: "accountant", label: "role.accountant" },
];

/**
 * Role colors for badges
 */
export const USER_ROLE_COLORS = {
  admin: "bg-purple-100 text-purple-800 border-purple-200",
  manager: "bg-blue-100 text-blue-800 border-blue-200",
  clientSupport: "bg-green-100 text-green-800 border-green-200",
  client: "bg-yellow-100 text-yellow-800 border-yellow-200",
  livreur: "bg-orange-100 text-orange-800 border-orange-200",
  accountant: "bg-indigo-100 text-indigo-800 border-indigo-200",
};

// ============================================================================
// USER STATUS OPTIONS
// ============================================================================

/**
 * User status options
 * Use translation keys for labels: t(`users.status.${value}`)
 */
export const USER_STATUS_OPTIONS = [
  { value: "active", label: "users.status.active" },
  { value: "inactive", label: "users.status.inactive" },
  { value: "suspended", label: "users.status.suspended" },
  { value: "pending", label: "users.status.pending" },
];

/**
 * Status colors for user badges
 */
export const USER_STATUS_COLORS = {
  active: "bg-green-100 text-green-800 border-green-200",
  inactive: "bg-gray-100 text-gray-800 border-gray-200",
  suspended: "bg-red-100 text-red-800 border-red-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
};

/**
 * User status values (constants)
 */
export const USER_STATUS_VALUES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  SUSPENDED: "suspended",
  PENDING: "pending",
};

// ============================================================================
// FORM FIELD NAMES
// ============================================================================

/**
 * User form field names
 */
export const USER_FORM_FIELDS = {
  FULL_NAME: "fullName",
  EMAIL: "email",
  PASSWORD: "password",
  CONFIRM_PASSWORD: "confirmPassword",
  PHONE: "phone",
  BRAND_NAME: "brandName",
  CITY: "city",
  ADDRESS: "address",
  WEBSITE: "website",
  ROLE: "role",
  STATUS: "status",
  PROFILE_IMAGE: "profileImage",
  ASSIGNED_CLIENTS: "assignedClients",
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * User validation constants
 */
export const USER_VALIDATION = {
  FULL_NAME_MIN_LENGTH: 4,
  FULL_NAME_MAX_LENGTH: 100,
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 50,
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 10,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  WEBSITE_PATTERN: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
};

// ============================================================================
// TOAST MESSAGE KEYS
// ============================================================================

/**
 * Toast message translation keys for user operations
 */
export const USER_TOAST_KEYS = {
  CREATING: "toast.creatingUser",
  CREATED_SUCCESS: "toast.userCreated",
  UPDATE_SUCCESS: "toast.userUpdated",
  DELETE_SUCCESS: "toast.userDeleted",
  PASSWORD_CHANGED: "users.passwordChanged",
  PROFILE_UPDATED: "users.profileUpdated",
  ASSIGNMENT_SUCCESS: "users.assignmentSuccess",
  STATUS_UPDATE_SUCCESS: "users.statusUpdated",
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * User-related route paths
 */
export const USER_ROUTES = {
  LIST: "/users",
  CREATE: "/users/create",
  EDIT: "/users/edit/:id",
  DETAILS: "/users/:id",
  PROFILE: "/profile",
  ACCOUNT: "/account",
  CHANGE_PASSWORD: "/account/change-password",
  CLIENT_SUPPORT_ASSIGN: "/users/client-support/:id/assign",
};

// ============================================================================
// FILTER CONFIGURATION
// ============================================================================

/**
 * User filter configuration
 */
export const USER_FILTER_CONFIG = {
  showRoleFilter: true,
  showStatusFilter: true,
  showCityFilter: true,
  showDateRangeFilter: true,
  entityName: "users",
};

// ============================================================================
// PLACEHOLDERS
// ============================================================================

/**
 * Placeholder text for user form fields (translation keys)
 */
export const USER_PLACEHOLDERS = {
  FULL_NAME: "users.form.fullNamePlaceholder",
  EMAIL: "users.form.emailPlaceholder",
  PASSWORD: "users.form.passwordPlaceholder",
  PHONE: "0600000000",
  BRAND_NAME: "users.form.brandNamePlaceholder",
  CITY: "users.form.cityPlaceholder",
  ADDRESS: "users.form.addressPlaceholder",
  WEBSITE: "users.websitePlaceholder",
};

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * React Query keys for user-related queries
 */
export const USER_QUERY_KEYS = {
  BASE: "users",
  DETAIL: "user",
  PROFILE: "profile",
  CLIENT_SUPPORT_USERS: "client-support-users",
  CLIENTS: "clients",
  LIVREURS: "livreurs",
  ASSIGNED_CLIENTS: "assigned-clients",
};

// ============================================================================
// EXPORT CONFIGURATION
// ============================================================================

/**
 * User export configuration
 */
export const USER_EXPORT_CONFIG = {
  FILE_NAME_PREFIX: "users",
  DATE_FORMAT: "yyyy-MM-dd",
  SHEET_NAME: "Users",
};

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================

/**
 * User list pagination settings
 */
export const USER_PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};

// ============================================================================
// PASSWORD STRENGTH
// ============================================================================

/**
 * Password strength indicators
 */
export const PASSWORD_STRENGTH = {
  WEAK: { min: 0, max: 2, label: "users.password.weak", color: "bg-red-500" },
  MEDIUM: {
    min: 3,
    max: 4,
    label: "users.password.medium",
    color: "bg-yellow-500",
  },
  STRONG: {
    min: 5,
    max: 6,
    label: "users.password.strong",
    color: "bg-green-500",
  },
};
